import * as React from "react";

import { ManageLogo } from "@app/access-reports/admin";
import {
  AccessReportsCoverPageElementEnum,
  AccessReportsWizardElement,
  AccessReportsWizardElementTypeEnum,
  AccessReportsWizardLogo,
  AccessReportsWizardModel,
  AccessReportsWizardOption,
  AccessReportsWizardPage,
  AccessReportsWizardStepEnum,
  AccessReportsWizardTemplate,
  AccessReportsWizardVersionModel
} from "@app/access-reports/shared";
import { QESButton, QESSelect, QESTextInput } from "@app/shared/qes-standards";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { flatten, isEqual } from "lodash";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ColumnConfig, Waffles, wafflesKeyColumnName } from "@app/shared/waffles";
import { JSX } from "react";

interface AccessReportsAdminCoverPageTabProps {
  accessReportsWizard: AccessReportsWizardModel;
  disableUserActions: boolean;
  onCoverPageDefaultValueChange: (elementId: string, event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => void;
  onCoverPageToggle: (elementId: string, key: keyof AccessReportsWizardElement) => void;
  onSaveClick: () => void;
  onSaveLogosClick: (logos: AccessReportsWizardOption[], reloadPage: boolean) => void;
  selectedVersion: AccessReportsWizardVersionModel;
}

interface CoverPageTableData {
  [wafflesKeyColumnName]: string;
  blank: JSX.Element;
  defaultFieldName: string;
  defaultValue: JSX.Element;
  disable: JSX.Element;
  hide: JSX.Element;
  manageLogos: JSX.Element;
}

const columnConfig: ColumnConfig = {
  blank: {
    displayText: "",
    noSort: true,
    testId: "blank",
  },
  defaultFieldName: {
    displayText: "Default Field Name",
    noSort: true,
    testId: "default-field-name",
  },
  defaultValue: {
    displayText: "Default Value",
    noSort: true,
    testId: "default-value",
  },
  disable: {
    displayText: "Disable",
    noSort: true,
    testId: "disable",
  },
  hide: {
    displayText: "Hide",
    noSort: true,
    testId: "hide",
  },
  manageLogos: {
    displayText: "",
    noSort: true,
    testId: "manage-logos",
  }
};

class AccessReportsAdminCoverPageTab extends React.Component<AccessReportsAdminCoverPageTabProps> {
  constructor(props: AccessReportsAdminCoverPageTabProps) {
    super(props);
  }

  public render() {
    const coverPageElements: AccessReportsWizardElement[] = this.getCoverPageElements(this.props.selectedVersion.template);

    return (
      <div className="tab-pane fade" id="cover-page">
        <div className="row">
          <div className="col-12">
            <QESButton
              className="float-right mb-3"
              disabled={this.disableSave(coverPageElements)}
              qesType="primary"
              qesTestId={"access-reports-admin__dashboard--cover-page-save"}
              onClick={() => this.props.onSaveClick()}
            >
              Save
            </QESButton>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            {this.buildCoverPageTable(coverPageElements)}
          </div>
        </div>
      </div>
    );
  }

  private disableSave = (inProgressCoverPageElements: AccessReportsWizardElement[]): boolean => {
    const currentlySavedTemplate: AccessReportsWizardTemplate = this.props.accessReportsWizard.accessReportWizardVersions.find(
      x => x.accessReportWizardVersionId === this.props.selectedVersion.accessReportWizardVersionId
    )?.template;
    const currentlySavedCoverPageElements: AccessReportsWizardElement[] = this.getCoverPageElements(currentlySavedTemplate);

    if (isEqual(currentlySavedCoverPageElements, inProgressCoverPageElements) || this.props.disableUserActions) {
      return true;
    }

    return false;
  }

  private buildCoverPageTable = (coverPageElements: AccessReportsWizardElement[]): React.ReactNode => {
    const data: CoverPageTableData[] = coverPageElements.map((element, index) => ({
      /* eslint-disable sort-keys */
      [wafflesKeyColumnName]: this.getTitle(element),
      defaultFieldName: this.getTitle(element),
      hide: (
        <QESCheckbox
          checked={element.hidden}
          className="m-1"
          disabled={this.props.disableUserActions}
          label=""
          testId={`access-reports-admin__cover-page--hide-checkbox-${index}`}
          onChange={() => this.props.onCoverPageToggle(element.elementId, "hidden")}
        />
      ),
      disable: (
        <QESCheckbox
          checked={element.disabled}
          className="m-1"
          disabled={this.props.disableUserActions}
          label=""
          testId={`access-reports-admin__cover-page--disable-checkbox-${index}`}
          onChange={() => this.props.onCoverPageToggle(element.elementId, "disabled")}
        />
      ),
      defaultValue: (
        element.elementType === AccessReportsWizardElementTypeEnum.Dropdown ? (
          <QESSelect
            allowEmpty={true}
            disabled={this.props.disableUserActions}
            testId={`access-reports-admin__cover-page--dropdown-${index}`}
            hideLabel={true}
            label={element.title}
            onChange={(e) => this.props.onCoverPageDefaultValueChange(element.elementId, e)}
            value={(element.defaultValue?.toString()) || ""}
          >
            {element.options.map((option, optionIndex) => (
              <option
                key={optionIndex}
                value={option.id}
              >
                {option.displayName ? option.displayName : option.name}
              </option>
            ))}
          </QESSelect>
        ) : (
          <QESTextInput
            testId={`access-reports-admin__cover-page--input-${index}`}
            disabled={this.props.disableUserActions || element.elementId === AccessReportsCoverPageElementEnum.To}
            hideLabel={true}
            label={element.title}
            onChange={(e) => this.props.onCoverPageDefaultValueChange(element.elementId, e)}
            value={element.defaultValue as string || ""}
          />
        )
      ),
      manageLogos: (
        (element.elementId === "logo") ? (
          <FlyoutLink
            classNames="qes-button-secondary"
            content={{
              body: this.renderManageLogosSidePanel(),
              title: `${this.props.disableUserActions ? "View" : "Manage"} Logos`,
            }}
            panelId="access-reports-admin__products--manage-logos"
            size="20"
            testId="access-reports-admin__products--manage-logos-link"
          >
            {this.props.disableUserActions ? "View" : "Manage"} Logos
          </FlyoutLink>
        ) : (
          <>&nbsp;</>
        )
      ),
      blank: <>&nbsp;</>,
      /* eslint-enable */
    }));
    return (
      <div className="access-reports-wizard-admin-table">
        <Waffles<CoverPageTableData>
          columns={columnConfig}
          data={data}
          disableProcessingOverlay={true}
          fullWidth={true}
          testId={"access-reports-admin-cover-page-tab"}
        />
      </div>
    );
  }

  private getCoverPageElements = (template: AccessReportsWizardTemplate): AccessReportsWizardElement[] => {
    let coverPageElements: AccessReportsWizardElement[] = [];
    const coverPage: AccessReportsWizardPage = template.pages.find((x) => x.title === AccessReportsWizardStepEnum.CoverPage);

    if (!!coverPage) {
      for (const formGroup of coverPage.formGroups) {
        // remove product-related elements from this page
        const filteredElements: AccessReportsWizardElement[] = formGroup.elements.filter((x) => !x.elementId.includes("product"));
        coverPageElements = coverPageElements.concat(filteredElements);
      }
    }

    return coverPageElements;
  }

  private renderManageLogosSidePanel = (): JSX.Element => {
    const accessReportsWizardTemplate: AccessReportsWizardTemplate = this.props.selectedVersion.template;
    const coverPage: AccessReportsWizardPage = accessReportsWizardTemplate?.pages.find(
      x => x.title === AccessReportsWizardStepEnum.CoverPage
    );
    const coverPagePageElements: AccessReportsWizardElement[] = flatten(coverPage?.formGroups.map(x => x.elements));
    const logoElement: AccessReportsWizardElement = coverPagePageElements?.find(
      x => x.elementId === "logo"
    );

    if (!logoElement || !logoElement.options) {
      console.error("Unable to retrieve Manage Logos Side Panel");
    }

    return (
      <ManageLogo
        disableUserActions={this.props.disableUserActions}
        onSaveLogosClick={this.props.onSaveLogosClick}
        logos={logoElement.options as AccessReportsWizardLogo[]}
      />
    );
  }

  private getTitle = (element: AccessReportsWizardElement): string => {
    if (!!element.title) {
      return element.title;
    } else {
      const splitKey: string = element.elementId.replace(/([A-Z])/g, " $1");
      const formattedKey: string = (splitKey.charAt(0).toUpperCase() + splitKey.slice(1));

      return formattedKey;
    }
  }
}

export { AccessReportsAdminCoverPageTab };
